import dayjs from 'dayjs';
import _ from 'lodash';
import { DataFeedDataProvider } from '@tradingblock/tradingview';
import { ApiFactory, GetBarsOptions, Bar, ITradingBlockApi } from '@tradingblock/api';
import { stringOrQuery } from '@tradingblock/types';
import { MarketCloseTime } from '../../utilities/date';

export type ApiType = ReturnType<typeof ApiFactory>;

interface PeriodBar {
  from: Date;
  to: Date;
  bars: Bar[];
  timestamp: number; // When this data was cached
}
interface PeriodBars {
  [period: string]: PeriodBar;
}
interface CachedBars {
  [symbol: string]: PeriodBars;
}
const getError = (reason: string) => ({ reason });

// Cache TTL in milliseconds (5 minutes for intraday, 1 hour for daily)
const getCacheTTL = (period: string): number => {
  if (period === 'Day') {
    return 60 * 60 * 1000; // 1 hour for daily data
  }
  return 5 * 60 * 1000; // 5 minutes for intraday data
};

const getAllBarsForPeriod = async (
  api: ITradingBlockApi,
  symbol: string,
  options: Omit<GetBarsOptions<string | Date>, 'symbol'>
) => {
  const { starttime, endtime, period, bars } = options;
  const start = dayjs(starttime || new Date());
  const end = endtime ? dayjs(endtime) : undefined;

  const tryGetBars = (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
    return api.getBars({
      symbol,
      period,
      bars,
      starttime: from.toDate(),
      endtime: to.toDate(),
    });
  };
  const response = await tryGetBars(start, end || dayjs(new Date()));
  const { payload, errors } = response;

  // Detect HTTP 429 rate limiting bubbled up as a generic error message
  const firstError = Array.isArray(errors) ? String(errors[0] || '') : '';
  if (!payload && firstError.toLowerCase().includes('status code 429')) {
    const rateLimitError = new Error('RateLimited:429');
    rateLimitError.name = 'RateLimitError';
    throw rateLimitError;
  }

  // If any error is present (e.g., CORS preflight failure, network error), propagate as an error so callers
  // can break request loops instead of interpreting it as empty data.
  if (Array.isArray(errors) && errors.length > 0) {
    const err = new Error(`GetBarsError:${firstError || 'Unknown'}`);
    err.name = 'GetBarsError';
    throw err;
  }

  let barResult: Bar[] = payload ? [...payload] : [];
  // let minDateValue = _(barResult)
  //   .map(b => dayjs(b.D).valueOf())
  //   .min();
  // let requestCount = 1;
  // while (minDateValue !== undefined && minDateValue > start.valueOf() && requestCount < 5) {
  //   requestCount++;
  //   const currEnd = dayjs(minDateValue);
  //   const moreBarResults = await tryGetBars(start.add(-1, 'day'), currEnd);
  //   barResult.concat(moreBarResults.Payload);
  //   minDateValue = _(barResult)
  //     .map(b => dayjs(b.D).valueOf())
  //     .min();
  // }
  return barResult;
};

export class CachedApi implements DataFeedDataProvider {
  private api: ITradingBlockApi;
  private cache: CachedBars;
  private pendingRequests: Map<string, Promise<Bar[]>>;
  private rateLimitResetAt?: number;

  public constructor(tbapi: ITradingBlockApi) {
    this.api = tbapi;
    if (!tbapi) {
      throw getError('API cannot be undefined');
    }
    this.cache = {};
    this.pendingRequests = new Map();
  }
  public get marketClose() {
    return MarketCloseTime().toDate();
  }
  public getSymbols = (query: stringOrQuery, limit: number = 10) => this.api.getSymbols(query, limit);
  public getBars = async (options: GetBarsOptions<string | Date>) => {
    const { symbol, starttime, endtime, period } = options;
    const symbolVal = _.isString(symbol) ? symbol : _.first(symbol);
    if (symbolVal === undefined) {
      throw getError('symbol must be set');
    } else if (typeof symbol !== 'string') {
      console.warn('symbol isnt a string', symbol);
    }
    const cacheKey = `${symbolVal}-${period}`;
    const symbolData = this.cache[cacheKey];

    const start = dayjs(starttime || new Date());
    if (starttime === undefined) {
      throw getError('starttime must be set');
    }
    const end = dayjs(endtime || new Date());
    const symbolPeriodData = symbolData ? _.get(symbolData, period, undefined) : undefined;

    // Global cooldown if we've recently been rate limited
    if (this.rateLimitResetAt && Date.now() < this.rateLimitResetAt) {
      const secondsRemaining = Math.max(0, Math.ceil((this.rateLimitResetAt - Date.now()) / 1000));
      throw new Error(`RateLimited:cooldown ${secondsRemaining}s`);
    }
    if (
      symbolPeriodData &&
      dayjs(symbolPeriodData.from).valueOf() <= start.valueOf() &&
      dayjs(symbolPeriodData.to).valueOf() >= end.valueOf()
    ) {
      // Check if cache is still valid
      const cacheAge = Date.now() - symbolPeriodData.timestamp;
      const cacheTTL = getCacheTTL(period);

      if (cacheAge < cacheTTL) {
        const cachedResults =
          starttime === undefined
            ? symbolPeriodData.bars
            : _.filter(symbolPeriodData.bars, b => dayjs(b.D) >= start && dayjs(b.D) <= end);
        if (cachedResults !== undefined && cachedResults.length !== 0) {
          console.info(
            `Using cached data for ${symbol} - ${period}. CachedData length: ${cachedResults.length}, age: ${Math.round(
              cacheAge / 1000
            )}s`
          );
          return cachedResults;
        }
      } else {
        console.info(
          `Cache expired for ${symbol} - ${period}. Age: ${Math.round(cacheAge / 1000)}s, TTL: ${Math.round(
            cacheTTL / 1000
          )}s`
        );
      }
    }

    // Create a unique request key for deduplication
    const requestKey = `${cacheKey}-${start.valueOf()}-${end.valueOf()}`;

    // Check if there's already a pending request for this data
    if (this.pendingRequests.has(requestKey)) {
      console.info(`Request already pending for ${symbol} - ${period}, waiting for existing request`);
      return this.pendingRequests.get(requestKey)!;
    }

    // Create the request promise
    const requestPromise = getAllBarsForPeriod(this.api, symbolVal, options)
      .then(barResults => {
        return _.map(barResults, b => {
          return {
            ...b,
            //open
            O: _.round(b.O, 2),
            //high
            H: _.round(b.H, 2),
            //low
            L: _.round(b.L, 2),
            //close
            C: _.round(b.C, 2),
            //volume
            V: _.round(b.V, 2),
          };
        });
      })
      .then(bars => {
        // Update cache
        this.cache = {
          ...this.cache,
          [cacheKey]: {
            ...symbolData,
            [period]: {
              from: start.toDate(),
              to: end.toDate(),
              bars,
              timestamp: Date.now(),
            },
          },
        };
        return bars;
      })
      .catch(err => {
        const message = err && err.message ? String(err.message) : '';
        if (message.includes('RateLimited:429')) {
          // Default cooldown of 60s when rate limited
          this.rateLimitResetAt = Date.now() + 60 * 1000;
        }
        throw err;
      })
      .finally(() => {
        // Clean up pending request
        this.pendingRequests.delete(requestKey);
      });

    // Store the pending request
    this.pendingRequests.set(requestKey, requestPromise);

    return requestPromise;
  };
  public getQuotes = async (symbols: string[]) => {
    const { payload } = await this.api.quotes.getChartingFeed(symbols);
    return payload;
  };
}
